"use client"
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import {
    useAddProductPicturesMutation,
    useDeletePictureMutation,
    useLazyDownloadPicturesZipQuery,
    useSetProductPictureIsActiveMutation
} from "@/redux/apis";
import getApiUrl from "@/lib/project-urls";
import * as React from "react";
import {memo} from "react";
import FaIcon from "@/components/utilities/fa-icon";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import {unixTimeToTimeAndDate} from "@/lib/helper-functions";
import useIsInRoles from "@/hooks/useIsInRoles";
import {useProductImages} from "@/lib/api";
import {PIImageManagementAdminProps, ProductPicture} from "@/types/product";
import Switch from "@mui/material/Switch";
import PictureWithBlur from "@/components/utilities/picture-with-blur";


function PIImageManagementAdmin({productId, mutateProduct}: PIImageManagementAdminProps) {
    const isAdminOrPhotographer = useIsInRoles(["Admin", "Photographer"]);

    const theme = useTheme()

    const {data, mutate} = useProductImages(
        productId.toString(),
        !isAdminOrPhotographer
            ? {
            fallbackData: undefined,
                revalidateOnMount: false}
            : {
                revalidateOnFocus: false,
                shouldRetryOnError: false,
                dedupingInterval: 30000,
            }
    );

    const pictures = (data ?? []) as ProductPicture[]

    const [setActive] = useSetProductPictureIsActiveMutation()
    const [addPictures] = useAddProductPicturesMutation()
    const [deletePicture] = useDeletePictureMutation()
    const [createZip] = useLazyDownloadPicturesZipQuery();

    async function forceDownload(url: string, filename: string) {
        try {
            const response = await fetch(url, {credentials: "include"});
            if (!response.ok) throw new Error("Failed to fetch file");
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed:", err);
        }
    }

    async function downloadResource() {
        try {
            const res = await createZip(productId).unwrap();
            const url = getApiUrl(res.zipFilePath);
            if (!url) throw new Error("Invalid URL");
            const filename = res.zipFilePath.split("/").pop() || "download.zip";
            await forceDownload(url, filename);
        } catch (error) {
            console.error("Error downloading resource:", error);
        }
    }

    async function handleDownloadAll() {
        try {
            await downloadResource();
        } catch (err) {
            console.error("Zip not available, fallback needed");
            alert("دانلود همه فقط به صورت زیپ امکان‌پذیر است. لطفاً تکی دانلود کنید.");
        }
    }


    return (
        <>
            <Box display = "flex" alignItems = "end" mt = {-5} >

                <Box flexGrow = {1} />
                <Button
                    size = "small"
                    variant = "outlined"
                    sx = {{mx: 1}}
                    onClick = {handleDownloadAll}
                    startIcon = {<FaIcon faIcon = "fa-download" />}
                >
                    دانلود
                </Button >

                <Button variant = "contained" size = "small"
                    component = 'label' startIcon = {<FaIcon faIcon = "fa-upload" />} >
                    آپلود
                    <input
                        multiple
                        type = "file"
                        onChange = {async event => {
                            const input = event.currentTarget;
                            const images = input.files;

                            if (!images?.length) return;

                            try {
                                await addPictures({images, productId}).unwrap();
                                await Promise.all([mutate(), mutateProduct()]);
                                input.value = "";
                            } catch (error) {
                                console.error("Product image upload failed:", error);
                            }
                        }}
                        hidden
                    />
                </Button >

            </Box >

            <Stack divider = {<Divider />} spacing = {2} mt = {2} maxHeight={400} minHeight={50} overflow="auto">

            {
                    pictures?.map(item => (
                        <Stack
                            key = {item.id}
                            direction = {{xs: 'row', md: 'row'}}
                            alignItems = "center"
                            justifyContent = "space-between"
                            width = '100%'
                            gap={1}
                        >
                            <Box width = {80} >
                                <PictureWithBlur src = {item?.path} width = {80} height = {80} alt = "image_preview" />
                            </Box >
                            <Box mr={{md:0,xs:2}}>

                            <Typography color = {theme.palette.grey[500]} fontSize = {12} >
                                    اسم فایل</Typography >
                                <Typography mb = {1} fontFamily = 'sans-serif' noWrap fontWeight = {500} >
                                    {item.path}
                                </Typography >
                                <Typography color = {theme.palette.grey[500]} fontSize = {12} >
                                    تاریخ اضافه شدن
                                </Typography >
                                <Typography noWrap >{unixTimeToTimeAndDate(item.createdAt)}</Typography >
                            </Box >

                            <Box gap={1} display="flex">
                                {
                                    item.isActive ?
                                        "فعال"
                                        :
                                        "غیرفعال"
                                }

                            <Switch
                                checked = {item.isActive}
                                onChange = {async event => {
                                    const isActive = event.target.checked;
                                    await setActive({id: item.id, isActive}).unwrap();
                                    mutate();
                                    mutateProduct();
                                }}
                            />
                            </Box>

                            <Button
                                sx = {{minWidth: 60}}
                                onClick = {() => {
                                    const url = getApiUrl(item.path);
                                    if (!url) return;
                                    const filename = item.path.split('/').pop() || "file";
                                    forceDownload(url, filename);
                                }}
                                // startIcon = {<FaIcon faIcon = "fa-download" />}
                            >
                                دانلود
                            </Button >

                            <Button
                                sx = {{minWidth: 60}}
                                color = "error"
                                onClick = {() => deletePicture(item.id)}
                                // startIcon = {<FaIcon faIcon = "fa-times" />}
                            >
                                حذف
                            </Button >
                        </Stack >
                    ))
                }

            </Stack >

        </ >
    )
}

export default memo(PIImageManagementAdmin)
